import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { AddIcon, Box, HStack, MinusIcon, Pressable } from 'native-base';

import { SwitchButton, Text } from '@pimm/base';
import { Lock01Icon } from '@pimm/base';
import { FormDataEquipmentItem, FormEquipmentProfile } from '../hooks/form-equipment-profile.hook';

const BadgeDefault = () => {
  const { t } = useTranslation();
  return (
    <Box bg="gray.100" px={1.5} py={0.5} rounded="sm">
      <Text size="xs" fontWeight={500} color="gray.500">
        {t('store_settings:equipment_profile_default_badge')}
      </Text>
    </Box>
  );
};

type PlusMinusButtonProps = ComponentProps<typeof Pressable> & {
  variant?: 'increment' | 'decrement';
  onPress: (x: number) => void;
};

const PlusMinusButton = ({ isDisabled, variant = 'increment', ..._props }: PlusMinusButtonProps) => {
  const isIncrement = variant === 'increment';
  return (
    <Pressable
      boxSize={7}
      roundedLeft={!isIncrement ? 'md' : undefined}
      roundedRight={isIncrement ? 'md' : undefined}
      alignItems="center"
      justifyContent="center"
      borderWidth={1}
      borderColor={isDisabled ? 'gray.200' : 'gray.300'}
      bg={isDisabled ? 'gray.100' : 'white'}
      isDisabled={isDisabled}
      {..._props}
    >
      {isIncrement ? (
        <AddIcon size={3} color={isDisabled ? 'gray.400' : 'gray.700'} />
      ) : (
        <MinusIcon size={3} color={isDisabled ? 'gray.400' : 'gray.700'} />
      )}
    </Pressable>
  );
};

type QuantityControlProps = {
  isDisabled?: boolean;
  min?: number;
  max?: number;
  value?: number;
  onChange: (_value: number) => void;
};

const QuantityControl = ({ isDisabled, max = 0, min = 0, value = 0, ..._props }: QuantityControlProps) => {
  const { t } = useTranslation();
  const atMin = value <= min;
  const atMax = value >= max;

  const handleChange = (x: number) => () => {
    _props.onChange(value + x);
  };

  return (
    <HStack space={2} alignItems="center">
      <Text size="sm" color="gray.500" lineHeight="xs">
        {t('store_settings:stepper_max', { n: max })}
      </Text>

      <HStack shadow={0} rounded="md" bg="white">
        <PlusMinusButton variant="decrement" isDisabled={atMin || isDisabled} onPress={handleChange(-1)} />
        <Box
          w={9}
          h={7}
          alignItems="center"
          justifyContent="center"
          borderTopWidth={1}
          borderBottomWidth={1}
          borderColor="gray.300"
          bg="white"
        >
          <Text size="sm" fontWeight={600} color="gray.900">
            {value}
          </Text>
        </Box>
        <PlusMinusButton isDisabled={atMax || isDisabled} onPress={handleChange(1)} />
      </HStack>
    </HStack>
  );
};

type EquipmentItemPath = `categories.${number}.items.${number}`;

type EquipmentProfileItemRowProps = {
  fieldPath: EquipmentItemPath;
  isDisabled?: boolean;
};

export const EquipmentProfileItemRow = ({ fieldPath, isDisabled }: EquipmentProfileItemRowProps) => {
  const { control, setValue } = useFormContext<FormEquipmentProfile>();
  const isEditEnabled = useWatch({ control, name: 'isEditEnabled' });
  const item = useWatch({ control, name: fieldPath }) as FormDataEquipmentItem;

  const { isCustomerEditable, isOverridden, quantity = 0, quantityMin = 0, quantityMax = 0 } = item ?? {};
  const isDirty = item.quantity !== item.value;

  const handleChange = (_value: number) => {
    if (_value < quantityMin || _value > quantityMax) return;
    setValue(`${fieldPath}.value`, _value, { shouldDirty: true });
  };

  return (
    <HStack w="full">
      <Box w={1} bg={isDirty ? 'blue.500' : 'transparent'} />
      <HStack flex={1} p={3} pr={4} alignItems="center">
        <Box flex={1} pr={4}>
          <Text size="md" color="gray.900">
            {item.title}
          </Text>
        </Box>

        {!isCustomerEditable && (
          <HStack alignItems="center" space={1.5}>
            <Lock01Icon size={4} color="gray.400" />
            <Box minW={6} alignItems="center">
              <Text size="sm" fontWeight={500} color="gray.700">
                {item.quantity}
              </Text>
            </Box>
          </HStack>
        )}

        {isCustomerEditable && !isEditEnabled && (
          <HStack alignItems="center" space={1.5}>
            {!isOverridden && <BadgeDefault />}
            <Box minW={6} alignItems="center">
              <Text size="sm" fontWeight={500} color="gray.700">
                {quantity}
              </Text>
            </Box>
          </HStack>
        )}

        {isCustomerEditable && isEditEnabled && (
          <HStack alignItems="center" space={1.5}>
            {isDirty ? <Box boxSize={2} rounded="full" bg="blue.500" /> : !isOverridden && <BadgeDefault />}
            {quantityMax === 1 ? (
              <SwitchButton
                isChecked={(item.value ?? quantity) > 0}
                isDisabled={isDisabled}
                onChange={isChecked => handleChange(isChecked ? quantityMax : 0)}
              />
            ) : (
              <QuantityControl min={quantityMin} max={quantityMax} value={item.value} isDisabled={isDisabled} onChange={handleChange} />
            )}
          </HStack>
        )}
      </HStack>
    </HStack>
  );
};
